import { tempoApi } from '@/lib/api/tempoApi';
import type { ItemSyncStatusResponse } from '@/lib/api/types/work-integrations';
import { chunkBy } from '@/lib/utils/common/chunkBy';

const ITEM_SYNC_STATUS_BATCH_LIMIT = 50;

export const getItemSyncStatuses = async ({
  itemIds,
}: {
  itemIds: Set<number>;
}): Promise<Record<number, ItemSyncStatusResponse>> => {
  if (itemIds.size === 0) return {};

  const chunks = chunkBy(Array.from(itemIds), ITEM_SYNC_STATUS_BATCH_LIMIT);
  const all: Record<number, ItemSyncStatusResponse> = {};

  for (const ids of chunks) {
    const response = await tempoApi().post('api/v1/work-integration/items/sync-status', {
      json: { itemIds: ids },
    });
    const part = await response.json<Record<number, ItemSyncStatusResponse>>();
    Object.assign(all, part);
  }

  return all;
};

export const requestItemSync = async ({
  itemId,
  integrationId,
}: {
  itemId: number;
  integrationId?: number;
}): Promise<void> => {
  await tempoApi().post(`api/v1/work-integration/items/${itemId}/sync`, {
    searchParams: integrationId !== undefined ? { integrationId } : undefined,
  });
};

const markItemAsPending = async ({
  itemId,
  integrationId,
}: {
  itemId: number;
  integrationId: number;
}): Promise<ItemSyncStatusResponse> => {
  const params = new URLSearchParams({ integrationId: String(integrationId) });
  return await tempoApi()
    .post(`api/v1/work-integration/items/${itemId}/sync-status/pending?${params.toString()}`)
    .json<ItemSyncStatusResponse>();
};

const replacePendingIntegration = async ({
  itemId,
  integrationId,
}: {
  itemId: number;
  integrationId: number;
}): Promise<ItemSyncStatusResponse> => {
  const params = new URLSearchParams({ integrationId: String(integrationId) });
  return await tempoApi()
    .put(`api/v1/work-integration/items/${itemId}/sync-status/pending?${params.toString()}`)
    .json<ItemSyncStatusResponse>();
};

export const ItemSyncStatusApi = {
  getItemSyncStatuses,
  requestItemSync,
  markItemAsPending,
  replacePendingIntegration,
} as const;
